import React, { useEffect, useState, forwardRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Alert,
  Snackbar,
  Autocomplete,
  Slide,
  CircularProgress,
  Portal,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material";
import { tokens } from "../../theme";
import { Formik } from "formik";
import * as yup from "yup";
import {
  createExpensesRecord,
  updateExpensesRecord,
} from "../../services/ExpenseService";
import { listProducts } from "../../services/ProductService";
import CustomDatePicker from "../CustomDatePicker";

// Transition for the Dialog (slides up)
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Transition for the Snackbar (slides in from the right)
function SlideTransition(props) {
  return <Slide {...props} direction="left" />;
}

const validationSchema = yup.object().shape({
  date: yup.string().required("Date is required"),
});

const ExpensesRecordForm = ({
  open,
  handleClose,
  onRecordAdded,
  onRecordEdited,
  editRecord,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const editMode = Boolean(editRecord);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [phase, setPhase] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      const response = await listProducts();
      setProducts(response.data);
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    if (editMode && editRecord) {
      // Prepopulate selectedProduct if productId exists in the editRecord
      const product =
        products.find((p) => p.id === editRecord.productId) || null;
      setSelectedProduct(product);
    }
  }, [editMode, editRecord, products]);

  const initialValues = {
    title: editRecord?.title || "",
    type: editRecord?.type || "",
    quantity: editRecord?.quantity || "",
    cost: editRecord?.cost || "",
    expensesDesc: editRecord?.expensesDesc || "",
    supplierName: editRecord?.supplierName || "N/A",
    date: editRecord?.date || "",
    productId: editRecord?.productId || "",
  };

  const handleFormSubmit = (values, { setSubmitting, resetForm }) => {
    setPhase("loading");
    return new Promise(async (resolve) => {
      try {
        if (!selectedProduct && (!values.title || values.title.trim() === "")) {
          console.error(
            "No product selected or custom title provided. Please select a product or enter a custom title.",
          );
          setPhase(null);
          setSnackbarMessage(
            "Please select a product or enter a custom title.",
          );
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
          resolve();
          return; // Prevent submission
        }

        const expensesData = {
          ...values,
          productId: selectedProduct ? selectedProduct.id : null,
        };

        let response;
        if (editMode) {
          response = await updateExpensesRecord(editRecord.id, expensesData);
        } else {
          response = await createExpensesRecord(expensesData);
        }

        // Keep loading state visible for 1.5 seconds
        setTimeout(() => {
          setPhase(null);
          resetForm();
          handleClose();

          // Refresh the table
          if (editMode) {
            onRecordEdited(response.data);
          } else {
            onRecordAdded(response.data);
          }

          // Show the snackbar slightly after the table refreshes
          setTimeout(() => {
            setSnackbarMessage(
              `Expenses record ${editMode ? "updated" : "created"} successfully!`,
            );
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            resolve();
          }, 300);
        }, 1500);
      } catch (error) {
        console.error(
          `Failed to ${editMode ? "update" : "create"} expenses record: `,
          error,
        );
        setPhase(null);
        setSnackbarMessage(
          `Failed to ${editMode ? "update" : "create"} expenses record. Please try again.`,
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        resolve();
      }
    });
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const dropdownPaperStyle = {
    backgroundColor:
      theme.palette.mode === "dark"
        ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(colors.primary[400].slice(3, 5), 16)}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.8)`
        : "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(16px)",
    border: `1px solid ${
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.15)"
        : "rgba(0, 0, 0, 0.1)"
    }`,
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 8px 32px 0 rgba(0, 0, 0, 0.4)"
        : "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
    borderRadius: "12px",
    mt: 1,
    "& .MuiAutocomplete-listbox": {
      padding: "8px",
      "& .MuiAutocomplete-option": {
        padding: "12px 20px",
        margin: "4px 8px",
        borderRadius: "8px",
        transition: "all 0.2s ease-in-out",
        color: colors.gray[100],
        fontWeight: 600,
        fontSize: "14px",
        "&[aria-selected='true']": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.15)"
              : "rgba(0, 0, 0, 0.08)",
          color: colors.blueAccent[500],
        },
        "&[aria-selected='true'].Mui-focused": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.12)",
        },
        "&:hover, &.Mui-focused": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.1)"
              : "rgba(0, 0, 0, 0.04)",
        },
        "&:active": { transform: "scale(0.95)" },
      },
    },
  };

  // Shared input styling
  const inputStyleProps = {
    "& .MuiFilledInput-root": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.05)"
          : "rgba(0,0,0,0.04)",
      borderRadius: "8px",
      border: "1px solid transparent",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
      },
      "&.Mui-focused": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
        borderColor: colors.blueAccent[500],
      },
    },
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
      {
        display: "none",
        WebkitAppearance: "none",
        margin: 0,
      },
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleClose();
        }}
        maxWidth="md"
        fullWidth
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          sx: {
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            backgroundImage: "none",
            borderRadius: "12px",
            boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.05)"
            }`,
          },
        }}
      >
        <DialogTitle
          textAlign="center"
          sx={{ fontSize: "1.25rem", fontWeight: "700", pt: 3, pb: 2 }}
        >
          {editMode ? "Edit Expenses Record" : "Add New Expenses Record"}
        </DialogTitle>

        <Divider
          sx={{
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.12)",
          }}
        />

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={handleFormSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            setFieldValue,
            handleSubmit,
            resetForm,
          }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent sx={{ p: 3 }}>
                <Box
                  display="grid"
                  gap="16px"
                  mt="8px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                >
                  {/* Product/Expense Title with Searchable Input */}
                  <Autocomplete
                    freeSolo
                    options={products.map((product) => product.productName)}
                    value={values.title}
                    slotProps={{ paper: { sx: dropdownPaperStyle } }}
                    onChange={(event, newValue) => {
                      setFieldValue("title", newValue);

                      // Find the product by name and update selectedProduct
                      const matchedProduct = products.find(
                        (product) => product.productName === newValue,
                      );
                      if (matchedProduct) {
                        setFieldValue("productId", matchedProduct.id);
                        setSelectedProduct(matchedProduct); // Update state with the full product object
                      } else {
                        setFieldValue("productId", null);
                        setSelectedProduct(null); // Clear selectedProduct if no match
                      }
                    }}
                    onInputChange={(event, newInputValue) => {
                      setFieldValue("title", newInputValue);

                      // Optional: Clear productId if the input doesn't match an existing product
                      if (
                        !products.some(
                          (product) => product.productName === newInputValue,
                        )
                      ) {
                        setFieldValue("productId", null);
                        setSelectedProduct(null); // Clear selectedProduct for custom inputs
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Product/Expense Title"
                        variant="filled"
                        name="title"
                        onBlur={handleBlur}
                        error={!!touched.title && !!errors.title}
                        helperText={touched.title && errors.title}
                        sx={{ ...inputStyleProps }}
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                          style: { borderRadius: "8px", fontSize: "0.95rem" },
                        }}
                        InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                      />
                    )}
                    sx={{
                      gridColumn: { xs: "span 4", sm: "span 2" },
                    }}
                  />

                  <Autocomplete
                    freeSolo // Enables manual input
                    options={["Cement", "Building Blocks"]} // Predefined options
                    value={values.type}
                    slotProps={{ paper: { sx: dropdownPaperStyle } }}
                    onChange={(event, newValue) =>
                      setFieldValue("type", newValue)
                    }
                    onInputChange={(event, newInputValue) =>
                      setFieldValue("type", newInputValue)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="filled"
                        label="Type"
                        name="type"
                        onBlur={handleBlur}
                        error={!!touched.type && !!errors.type}
                        helperText={touched.type && errors.type}
                        sx={{ ...inputStyleProps }}
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                          style: { borderRadius: "8px", fontSize: "0.95rem" },
                        }}
                        InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                      />
                    )}
                    sx={{
                      gridColumn: { xs: "span 4", sm: "span 2" },
                    }}
                  />

                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Description"
                    name="expensesDesc"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.expensesDesc}
                    error={!!touched.expensesDesc && !!errors.expensesDesc}
                    helperText={touched.expensesDesc && errors.expensesDesc}
                    sx={{ gridColumn: "span 4", ...inputStyleProps }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    min="0"
                    label="Quantity"
                    name="quantity"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.quantity}
                    error={!!touched.quantity && !!errors.quantity}
                    helperText={touched.quantity && errors.quantity}
                    sx={{
                      gridColumn: { xs: "span 4", sm: "span 2" },
                      ...inputStyleProps,
                    }}
                    inputProps={{ min: 0 }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Supplier"
                    name="supplierName"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.supplierName}
                    error={!!touched.supplierName && !!errors.supplierName}
                    helperText={touched.supplierName && errors.supplierName}
                    sx={{
                      gridColumn: { xs: "span 4", sm: "span 2" },
                      ...inputStyleProps,
                    }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Cost"
                    name="cost"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.cost}
                    error={!!touched.cost && !!errors.cost}
                    helperText={touched.cost && errors.cost}
                    sx={{
                      gridColumn: { xs: "span 4", sm: "span 2" },
                      ...inputStyleProps,
                    }}
                    inputProps={{ min: 0 }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <CustomDatePicker
                    label="Date"
                    name="date"
                    value={values.date}
                    onChange={(val) => setFieldValue("date", val)}
                    onBlur={handleBlur}
                    error={!!touched.date && !!errors.date}
                    helperText={touched.date && errors.date}
                    sx={{
                      gridColumn: { xs: "span 4", sm: "span 2" },
                    }}
                  />
                </Box>
              </DialogContent>

              <Divider
                sx={{
                  mb: 0,
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.12)",
                }}
              />

              <DialogActions
                sx={{
                  px: 3,
                  pb: 3,
                  pt: { xs: 2, sm: 3 },
                  gap: { xs: 2, sm: 2 },
                  flexDirection: { xs: "column-reverse", sm: "row" },
                  justifyContent: { xs: "center", sm: "flex-end" },
                  "& > :not(style) ~ :not(style)": {
                    ml: "0 !important",
                  },
                }}
              >
                <Button
                  onClick={() => {
                    resetForm();
                    handleClose();
                  }}
                  variant="outlined"
                  disabled={phase === "loading"}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    color: colors.gray[100],
                    borderColor: colors.gray[500],
                    textTransform: "none",
                    fontWeight: "600",
                    px: { xs: 2, sm: 4 },
                    py: { xs: 1.2, sm: 1.2 },
                    borderRadius: "8px",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      borderColor: colors.gray[300],
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.04)",
                    },
                    "&:active": {
                      transform: "scale(0.95)",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={phase === "loading"}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    backgroundColor: colors.blueAccent[600],
                    color: "#ffffff",
                    textTransform: "none",
                    fontWeight: "600",
                    px: { xs: 2, sm: 4 },
                    py: { xs: 1.2, sm: 1.2 },
                    borderRadius: "8px",
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 4px 10px rgba(0,0,0,0.3)"
                        : "0 4px 12px rgba(79, 70, 229, 0.2)",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      backgroundColor: colors.blueAccent[500],
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 6px 14px rgba(0,0,0,0.4)"
                          : "0 6px 16px rgba(79, 70, 229, 0.3)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "scale(0.95)",
                      boxShadow: "none",
                    },
                    "&.Mui-disabled": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.12)"
                          : "rgba(0,0,0,0.12)",
                      color:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.3)"
                          : "rgba(0,0,0,0.3)",
                      boxShadow: "none",
                    },
                  }}
                >
                  {phase === "loading" ? (
                    <>
                      {editMode ? "Updating" : "Creating"}&nbsp;
                      <CircularProgress
                        size={18}
                        sx={{ color: "inherit", ml: "8px" }}
                      />
                    </>
                  ) : editMode ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>

      <Portal>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          TransitionComponent={SlideTransition}
          sx={{ mt: 8, zIndex: 9999 }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{
              width: "100%",
              backgroundColor:
                snackbarSeverity === "success"
                  ? colors.greenAccent[700]
                  : colors.redAccent[700],
              color: "#ffffff",
              borderRadius: "8px",
              fontWeight: 600,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0px 4px 12px rgba(0, 0, 0, 0.4)"
                  : "0px 4px 12px rgba(0, 0, 0, 0.1)",
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Portal>
    </>
  );
};

export default ExpensesRecordForm;
